import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import constants from "../constants";
import { get, onValue, ref } from "firebase/database";
import { rtdb } from "../firebase";
import { debounce, isObject } from "lodash";

// subscription types
export enum ImmplifySubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED'
}

export enum ImmplifySubscriptionType {
  FREE = 'FREE',
  PAID = 'PAID'
}

export interface ISubscriptions {
  mode: 'Stripe';
  customer_id: string;
  subscription_status: ImmplifySubscriptionStatus;
  expiry?: number;
  product_id?: string;
  product_type?: string;
  purchased_at?: number;
  price?: string;
  plan_type?: string;
  plan_duration?: string;
  currency?: string;
  type?: ImmplifySubscriptionType;
  _latest_event_payload?: string; //event json stringified
}

export interface IPlanDetails {
  currency: string;           // e.g., "$"
  description: string;        // Plan description
  lookUpKey: string;          // Stripe or payment-related lookup key
  plan: 'Free' | 'Basic' | 'Premium';  // Plan name
  price: number;              // Base price
  queryPrice: number;         // Additional price for queries (optional, not in all objects)
  type?: 'monthly' | 'yearly'; // Plan type (optional for Free plan)
}

// Main pricing plans object
export interface IPricingPlans {
  FREE: IPlanDetails;
  BASIC_MONTHLY: IPlanDetails;
  BASIC_YEARLY: IPlanDetails;
  PREMIUM_MONTHLY: IPlanDetails;
  PREMIUM_YEARLY: IPlanDetails;
}


interface IPaymentContext {
  currentSubscriptionStatus: ISubscriptions | null;
  pricingPlans: IPricingPlans | null;
}

const defaultValue: IPaymentContext = {
  currentSubscriptionStatus: null,
  pricingPlans: null,
};

const Context = createContext<IPaymentContext>(defaultValue);

export const usePaymentContext = () => useContext(Context);

export const PaymentContextProvider: FC = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState<ISubscriptions | null>(defaultValue.currentSubscriptionStatus);
  const [pricingPlans, setPricingPlans] = useState<any>(defaultValue.pricingPlans);

  // Fetch current user's subscription status
  const onValueCallback = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      console.log("onValue start subscription");
      const url = `${constants.middleHost}/users/${currentUser.uid}/subscriptions/current`;
      const token = await currentUser.getIdToken();
      // fetch the image
      const res: Response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const { data } = await res.json();
      const subData: ISubscriptions = data;
      setCurrentSubscriptionStatus(subData);
      console.log("onValue end subscription");
    } catch (error) {
      console.error(error);
    }
  }, [currentUser]);

  const debouncedOnValue = debounce(onValueCallback, 500, {
    leading: false,
  });


  // Fetch pricing plans from rtdb firebase
  const getPricingPlans = useCallback(async () => {
    const plansRef = ref(rtdb, `constants/pricingPlans`);
    const snapshot = await get(plansRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      if (isObject(data)) {
        setPricingPlans(data);
      }
    }
  }, []);

  useEffect(() => {
    getPricingPlans();
  }
    , [getPricingPlans]);


  useEffect(() => {
    // Register onValue
    if (!currentUser?.uid) return;
    const customDocumentsRef = ref(
      rtdb,
      `stripe_customers/${currentUser.uid}`
    );
    // important: onValueCallback has to be throttled because we don't want to add load to fetch documents api in middle server
    // allow one call in one second
    onValueCallback();
    const unsubscribe = onValue(customDocumentsRef, debouncedOnValue);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);
  
  return (
    <Context.Provider
      value={{
        currentSubscriptionStatus,
        pricingPlans,
      }}
    >
      {children}
    </Context.Provider>
  );
};
